import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Store, ShoppingBag, Palette, Users, Zap, Shield, ChevronRight, ExternalLink, Star, Package, TrendingUp, CreditCard, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import platformApi from '@/utils/platformApi';

export default function PlatformLandingPage() {
  const [showcaseStores, setShowcaseStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [storesError, setStoresError] = useState(null);
  const [publicStores, setPublicStores] = useState([]);
  const [publicLoading, setPublicLoading] = useState(true);
  const [publicError, setPublicError] = useState(null);
  const [shopQuery, setShopQuery] = useState('');

  useEffect(() => {
    loadShowcaseStores();
    loadPublicStores();
  }, []);

  const loadShowcaseStores = async () => {
    try {
      setStoresLoading(true);
      setStoresError(null);
      const { data } = await platformApi.get('/platform/showcase-stores');
      setShowcaseStores(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load showcase stores:', error);
      setStoresError(error.message || 'Failed to load stores');
    } finally {
      setStoresLoading(false);
    }
  };

  const loadPublicStores = async () => {
    try {
      setPublicLoading(true);
      setPublicError(null);
      const { data } = await platformApi.get('/platform/public-stores');
      setPublicStores(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load public stores:', error);
      setPublicError(error.message || 'Failed to load stores');
    } finally {
      setPublicLoading(false);
    }
  };

  // Build a Set of showcase tenant_ids so the Shop-by-Store cards can flag
  // the "featured" ones with a star badge.
  const showcaseTenantIds = useMemo(
    () => new Set(showcaseStores.map((s) => s.tenant_id)),
    [showcaseStores],
  );

  const filteredPublicStores = useMemo(() => {
    const q = (shopQuery || '').trim().toLowerCase();
    const base = q
      ? publicStores.filter((s) =>
          (s.name || '').toLowerCase().includes(q)
          || (s.slug || '').toLowerCase().includes(q)
          || (s.tagline || '').toLowerCase().includes(q),
        )
      : publicStores;
    // Featured (showcase) stores bubble to the top of the list when the
    // customer isn't actively searching, so they see the platform's
    // curated retailers first.
    if (!q && showcaseTenantIds.size) {
      return [...base].sort((a, b) => {
        const af = showcaseTenantIds.has(a.tenant_id) ? 0 : 1;
        const bf = showcaseTenantIds.has(b.tenant_id) ? 0 : 1;
        if (af !== bf) return af - bf;
        return (a.name || '').localeCompare(b.name || '');
      });
    }
    return base;
  }, [publicStores, shopQuery, showcaseTenantIds]);

  return (
    <div className="min-h-screen bg-[#F5F3EF]">
      {/* Header */}
      <header className="border-b border-[#E8E4DC] bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-[#2C3E2D] rounded-lg flex items-center justify-center">
              <Store className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-bold text-[#2C3E2D]" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Golfshop.fit
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-[#5A6B5C] hover:text-[#4A6741]" data-testid="header-login-btn">
                Sign In
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="bg-[#2C3E2D] hover:bg-[#1a2a1b] text-white" data-testid="header-signup-btn">
                Open Your Store
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[#2C3E2D]" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 25% 50%, #4A6741 0%, transparent 50%), radial-gradient(circle at 75% 50%, #4A6741 0%, transparent 50%)' }} />
        <div className="relative container mx-auto px-6 py-24 text-center">
          <div className="inline-block mb-6 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/80 text-sm">
            Premium Golf Distribution Platform
          </div>
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto mb-10"
            data-testid="hero-steps-banner"
          >
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_9064b4ee-fc41-48fd-a277-b00a8d749ae9/artifacts/hwlkkta5_Banner1.png"
              alt="Step 1: Sign Up for a Free Store"
              className="w-full rounded-xl shadow-lg"
              data-testid="hero-step-1-image"
            />
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_9064b4ee-fc41-48fd-a277-b00a8d749ae9/artifacts/l16flh27_Banner2.png"
              alt="Step 2: Market Your Store"
              className="w-full rounded-xl shadow-lg"
              data-testid="hero-step-2-image"
            />
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_9064b4ee-fc41-48fd-a277-b00a8d749ae9/artifacts/3dclh5t5_Banner3.png"
              alt="Step 3: Make Money as an Affiliate"
              className="w-full rounded-xl shadow-lg"
              data-testid="hero-step-3-image"
            />
          </div>
          <p className="text-lg text-white/70 max-w-2xl mx-auto mb-10">
            Launch your own custom golf equipment store powered by Golfshop.fit's
            170+ premium shaft catalog. We handle inventory and fulfillment — you set
            your prices and build your brand.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button
                size="lg"
                className="bg-white text-[#2C3E2D] hover:bg-gray-100 px-8 py-6 text-lg font-bold"
                data-testid="hero-cta-signup"
              >
                Open Your Store
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="#how-it-works">
              <Button
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
                data-testid="hero-cta-learn"
              >
                See How It Works
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Shop by Store — customer-facing browse & pick a retailer */}
      <section className="bg-white py-16 border-b border-[#E8E4DC]" data-testid="shop-by-store-section">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#4A6741]/10 text-[#4A6741] text-xs font-bold mb-3">
                <ShoppingBag className="h-3.5 w-3.5" />
                Shop by Store
              </div>
              <h2 className="text-3xl font-bold text-[#2C3E2D]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Pick a store to shop from
              </h2>
              <p className="text-[#5A6B5C] mt-2 max-w-xl">
                Every store on Golfshop.fit carries the same premium catalog. Choose your favourite retailer and check out on their site.
              </p>
            </div>
            <div className="relative w-full md:w-80 shrink-0">
              <Search className="h-4 w-4 text-[#5A6B5C] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={shopQuery}
                onChange={(e) => setShopQuery(e.target.value)}
                placeholder="Search stores by name..."
                className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-[#E8E4DC] bg-[#F5F3EF] text-sm text-[#2C3E2D] placeholder-[#5A6B5C] focus:outline-none focus:ring-2 focus:ring-[#4A6741]/40 focus:border-[#4A6741]"
                data-testid="shop-search-input"
                aria-label="Search stores"
              />
            </div>
          </div>

          {publicLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse h-40 rounded-xl bg-[#F5F3EF]" />
              ))}
            </div>
          ) : publicError ? (
            <div className="max-w-md mx-auto text-center p-6 bg-[#FEF3C7] border border-[#F59E0B] rounded-lg" data-testid="shop-error">
              <p className="text-[#92400E] font-semibold text-sm mb-2">Couldn't load stores</p>
              <p className="text-[#A16207] text-xs mb-4">{publicError}</p>
              <button
                onClick={loadPublicStores}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#2C3E2D] hover:bg-[#1a2a1b] text-white text-sm font-semibold rounded-md"
                data-testid="shop-retry-btn"
              >
                Try Again
              </button>
            </div>
          ) : filteredPublicStores.length === 0 ? (
            <div className="text-center py-12 text-[#5A6B5C] text-sm" data-testid="shop-empty">
              No stores match &ldquo;<span className="font-semibold text-[#2C3E2D]">{shopQuery}</span>&rdquo;.
              {shopQuery && (
                <button
                  onClick={() => setShopQuery('')}
                  className="ml-2 text-[#4A6741] font-semibold hover:underline"
                  data-testid="shop-clear-search"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="shop-grid">
              {filteredPublicStores.map((store) => (
                <StorePickerCard
                  key={store.tenant_id}
                  store={store}
                  featured={showcaseTenantIds.has(store.tenant_id)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How It Works — Distributor Flow */}
      <section id="how-it-works" className="container mx-auto px-6 py-20">
        <h2 className="text-3xl font-bold text-[#2C3E2D] text-center mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
          How Golfshop.fit Distribution Works
        </h2>
        <p className="text-[#5A6B5C] text-center max-w-xl mx-auto mb-14">
          Start selling premium golf equipment in three simple steps — no warehouse, no upfront inventory costs.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          <StepCard
            number="1"
            title="Create Your Store"
            description="Sign up and customize your storefront with your own branding, logo, and colors. Your store, your identity."
          />
          <StepCard
            number="2"
            title="Subscribe to Fulfillment"
            description="For $20/month, unlock the full Golfshop.fit catalog — 170+ premium shafts and custom builds. Set your own retail prices."
          />
          <StepCard
            number="3"
            title="Sell & We Ship"
            description="When a customer orders, we handle the fulfillment. You earn the margin between your price and the cost price. Simple."
          />
        </div>
      </section>

      {/* Showcase Stores — always renders (with loading + empty states) so
          the section never silently disappears. */}
      <section className="bg-white py-20" data-testid="showcase-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
              <h2 className="text-3xl font-bold text-[#2C3E2D]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Featured Stores on Golfshop.fit
              </h2>
              <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
            </div>
            <p className="text-[#5A6B5C]">See what retailers are building on our platform</p>
          </div>
          {storesLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="showcase-loading">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse h-72 rounded-xl bg-[#F5F3EF]" />
              ))}
            </div>
          ) : storesError ? (
            <div className="max-w-md mx-auto text-center p-6 bg-[#FEF3C7] border border-[#F59E0B] rounded-lg" data-testid="showcase-error">
              <p className="text-[#92400E] font-semibold text-sm mb-2">Couldn&apos;t load featured stores</p>
              <p className="text-[#A16207] text-xs mb-4">{storesError}</p>
              <button
                onClick={loadShowcaseStores}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#2C3E2D] hover:bg-[#1a2a1b] text-white text-sm font-semibold rounded-md"
                data-testid="showcase-retry-btn"
              >
                Try Again
              </button>
            </div>
          ) : showcaseStores.length === 0 ? (
            <p className="text-center text-sm text-[#5A6B5C]" data-testid="showcase-empty">
              No featured stores yet — check back soon.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="showcase-grid">
              {showcaseStores.map((store) => (
                <ShowcaseCard key={store.tenant_id} store={store} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Value Props */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#2C3E2D] text-center mb-12" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Why Retailers Choose Golfshop.fit
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Package className="h-8 w-8" />}
              title="Zero Inventory Risk"
              description="No need to buy stock upfront. Our catalog is your catalog — we warehouse and ship everything."
            />
            <FeatureCard
              icon={<TrendingUp className="h-8 w-8" />}
              title="Set Your Own Prices"
              description="You control your retail pricing. The difference between your price and our cost price is your profit."
            />
            <FeatureCard
              icon={<Zap className="h-8 w-8" />}
              title="AI Shaft Fitting"
              description="Built-in AI-powered fitting wizard. Customers upload launch monitor data and get personalized recommendations."
            />
            <FeatureCard
              icon={<Palette className="h-8 w-8" />}
              title="Your Brand, Your Store"
              description="Full branding control — custom logos, colors, banners, and your own domain. Customers see your brand, not ours."
            />
            <FeatureCard
              icon={<CreditCard className="h-8 w-8" />}
              title="Stripe-Powered Payments"
              description="Accept credit cards securely via Stripe Connect. Funds go directly to your account — we never touch your revenue."
            />
            <FeatureCard
              icon={<Shield className="h-8 w-8" />}
              title="Pre-Owned Flexibility"
              description="Sell our catalog and your own pre-owned inventory side by side. Full control over your unique product listings."
            />
          </div>
        </div>
      </section>

      {/* Pricing Highlight */}
      <section className="container mx-auto px-6 py-20">
        <h2 className="text-3xl font-bold text-[#2C3E2D] text-center mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Simple, Transparent Pricing
        </h2>
        <p className="text-[#5A6B5C] text-center mb-12">
          Start with a 7-day free trial. No commitment. Cancel anytime.
        </p>
        <div className="grid grid-cols-1 gap-8 max-w-md mx-auto">
          {/* Fulfillment Plan (only plan available for now) */}
          <div className="bg-[#2C3E2D] rounded-2xl p-8 relative text-white" data-testid="pricing-fulfillment">
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Fulfillment</h3>
            <div className="mb-4">
              <span className="text-4xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>$20</span>
              <span className="text-white/60">/month</span>
            </div>
            <div className="inline-block mb-6 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-bold">
              7-day free trial
            </div>
            <ul className="text-sm text-white/80 space-y-2.5 mb-8">
              <li className="flex items-center gap-2"><span className="text-[#8BC34A]">&#10003;</span> 170+ premium shaft catalog</li>
              <li className="flex items-center gap-2"><span className="text-[#8BC34A]">&#10003;</span> Set your own retail prices</li>
              <li className="flex items-center gap-2"><span className="text-[#8BC34A]">&#10003;</span> Custom branding & storefront</li>
              <li className="flex items-center gap-2"><span className="text-[#8BC34A]">&#10003;</span> AI shaft fitting wizard</li>
              <li className="flex items-center gap-2"><span className="text-[#8BC34A]">&#10003;</span> Stripe payments direct to you</li>
              <li className="flex items-center gap-2"><span className="text-[#8BC34A]">&#10003;</span> We handle fulfillment & shipping</li>
              <li className="flex items-center gap-2"><span className="text-[#8BC34A]">&#10003;</span> Zero inventory risk</li>
              <li className="flex items-center gap-2"><span className="text-[#8BC34A]">&#10003;</span> Sell pre-owned alongside catalog</li>
            </ul>
            <Link to="/signup?plan=fulfillment">
              <Button className="w-full bg-white text-[#2C3E2D] hover:bg-gray-100 py-5 font-bold" data-testid="pricing-cta-fulfillment">
                Open Your Store
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#2C3E2D] py-20">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Ready to Sell Premium Golf Equipment?
          </h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">
            Open your storefront in minutes. Subscribe to our catalog and start earning on every sale — we handle the rest.
          </p>
          <Link to="/signup">
            <Button
              size="lg"
              className="bg-white text-[#2C3E2D] hover:bg-gray-100 px-8 py-6 text-lg font-bold"
              data-testid="footer-cta-signup"
            >
              Open Your Store
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E8E4DC] bg-white py-8">
        <div className="container mx-auto px-6 text-center text-[#5A6B5C] text-sm">
          <p>&copy; {new Date().getFullYear()} Golfshop.fit. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function StepCard({ number, title, description }) {
  return (
    <div className="text-center">
      <div className="h-14 w-14 mx-auto mb-4 rounded-full bg-[#2C3E2D] text-white flex items-center justify-center text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
        {number}
      </div>
      <h3 className="text-lg font-bold text-[#2C3E2D] mb-2">{title}</h3>
      <p className="text-[#5A6B5C] text-sm">{description}</p>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="bg-[#F5F3EF] rounded-xl p-6 border border-[#E8E4DC] hover:shadow-lg transition-shadow">
      <div className="h-14 w-14 bg-[#2C3E2D]/10 rounded-lg flex items-center justify-center text-[#2C3E2D] mb-4">
        {icon}
      </div>
      <h3 className="text-lg font-bold text-[#2C3E2D] mb-2">{title}</h3>
      <p className="text-[#5A6B5C] text-sm">{description}</p>
    </div>
  );
}

function StorePickerCard({ store, featured }) {
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      if (backend) {
        const configuredOrigin = new URL(backend).origin;
        if (configuredOrigin === window.location.origin) return `${backend}${url}`;
      }
    } catch (e) {
      console.warn('Failed to resolve asset URL origin:', e);
    }
    return url;
  };

  const logoUrl = getImageUrl(store.logo_url);
  const bannerUrl = getImageUrl(store.storefront_preview_url || store.banner_image_url);
  const primary = store.primary_color || '#2C3E2D';

  return (
    <a
      href={`/${store.slug}`}
      className="group relative block rounded-xl border border-[#E8E4DC] bg-white overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all"
      data-testid={`shop-store-${store.slug}`}
    >
      {featured && (
        <span
          className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 rounded-full bg-yellow-400/95 text-yellow-900 text-[10px] font-bold px-2 py-0.5 shadow"
          data-testid={`shop-featured-${store.slug}`}
        >
          <Star className="h-3 w-3 fill-yellow-900" />
          Featured
        </span>
      )}
      <div
        className="relative h-28 flex items-center justify-center overflow-hidden"
        style={{ backgroundColor: primary }}
      >
        {bannerUrl ? (
          <img
            src={bannerUrl}
            alt={`${store.name} storefront`}
            className="w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : null}
        <div className="absolute inset-0 flex items-center justify-center">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={store.name}
              className="h-14 w-14 object-contain rounded-lg bg-white/95 p-1.5 shadow-md"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <div className="h-14 w-14 bg-white/95 rounded-lg flex items-center justify-center shadow-md">
              <Store className="h-7 w-7" style={{ color: primary }} />
            </div>
          )}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-bold text-[#2C3E2D] group-hover:text-[#4A6741] transition-colors text-sm leading-tight">
          {store.name}
        </h3>
        {store.tagline ? (
          <p className="text-xs text-[#5A6B5C] mt-1 line-clamp-2">{store.tagline}</p>
        ) : (
          <p className="text-xs text-[#5A6B5C] mt-1">Powered by Golfshop.fit</p>
        )}
        <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#4A6741] group-hover:gap-2 transition-all">
          Shop this store
          <ChevronRight className="h-3.5 w-3.5" />
        </div>
      </div>
    </a>
  );
}

function ShowcaseCard({ store }) {
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    // /uploads/... lives on whatever origin serves the app (same origin on custom domains,
    // cross-origin on preview). Prepend the configured backend origin only when it matches
    // the current origin; otherwise use a same-origin relative path so images load on prod.
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      if (backend) {
        const configuredOrigin = new URL(backend).origin;
        if (configuredOrigin === window.location.origin) return `${backend}${url}`;
      }
    } catch (e) {
      console.warn('Failed to resolve asset URL origin:', e);
    }
    return url;
  };

  const previewImage = getImageUrl(store.storefront_preview_url || store.banner_image_url);
  const logoUrl = getImageUrl(store.logo_url);

  return (
    <a
      href={`/${store.slug}`}
      target="_blank"
      rel="noopener noreferrer"
      className="block bg-white rounded-xl border border-[#E8E4DC] overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1 group"
      data-testid={`showcase-card-${store.slug}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
        {previewImage ? (
          <img
            src={previewImage}
            alt={`${store.name} homepage preview`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            onError={(e) => {
              e.target.style.display = 'none';
              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div
          className={`absolute inset-0 flex flex-col items-center justify-center ${previewImage ? 'hidden' : 'flex'}`}
          style={{ backgroundColor: store.primary_color || '#2C3E2D' }}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={store.name}
              className="h-16 w-16 object-contain rounded-lg bg-white p-2 mb-3"
              onError={(e) => e.target.style.display = 'none'}
            />
          ) : (
            <div className="h-16 w-16 bg-white/20 rounded-lg flex items-center justify-center mb-3">
              <Store className="h-8 w-8 text-white" />
            </div>
          )}
          <span className="text-white/80 text-sm">Preview not available</span>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="absolute top-3 right-3 bg-white/90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
          <ExternalLink className="h-4 w-4 text-[#2C3E2D]" />
        </div>
      </div>
      <div className="p-4 border-t border-[#E8E4DC]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                className="h-8 w-8 object-contain rounded"
                onError={(e) => e.target.style.display = 'none'}
              />
            ) : (
              <div
                className="h-8 w-8 rounded flex items-center justify-center"
                style={{ backgroundColor: store.primary_color || '#2C3E2D' }}
              >
                <Store className="h-4 w-4 text-white" />
              </div>
            )}
            <div>
              <h3 className="font-bold text-[#2C3E2D] group-hover:text-[#4A6741] transition-colors">
                {store.name}
              </h3>
              <p className="text-xs text-[#5A6B5C]">Powered by Golfshop.fit</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-[#5A6B5C] group-hover:text-[#4A6741] group-hover:translate-x-1 transition-all" />
        </div>
      </div>
    </a>
  );
}
